import { Card, Row, Col, Statistic, Spin, Button, theme } from 'antd';
import { ShoppingOutlined, WarningOutlined, ExclamationCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

interface Part {
  id: string;
  currentStockLevel?: number | null;
  optimalStockLevel?: number | null;
  unitPrice?: string | null;
}

interface PartsData {
  items: Part[];
  pagination: {
    total: number;
  };
}

interface PartsSummaryCardProps {
  data?: PartsData;
  loading?: boolean;
}

export function PartsSummaryCard({ data, loading }: PartsSummaryCardProps) {
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const parts = data?.items || [];
  const totalParts = data?.pagination?.total || 0;

  // 在庫切れ部品・消耗品数
  const outOfStockCount = parts.filter((p) => (p.currentStockLevel ?? 0) === 0).length;

  // 在庫不足部品・消耗品数（現在在庫 < 適正在庫）
  const lowStockCount = parts.filter((p) => {
    const current = p.currentStockLevel ?? 0;
    const optimal = p.optimalStockLevel ?? 0;
    return current > 0 && current < optimal;
  }).length;

  // 在庫総額
  const totalStockValue = parts.reduce((sum, part) => {
    const price = parseFloat(part.unitPrice || '0');
    const quantity = part.currentStockLevel ?? 0;
    return sum + price * quantity;
  }, 0);

  return (
    <Card
      title="部品・消耗品在庫サマリー"
      extra={
        <Button type="link" icon={<ShoppingOutlined />} onClick={() => navigate('/app/stock-dashboard')}>
          在庫ダッシュボード
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Statistic
                title="総部品・消耗品数"
                value={totalParts}
                suffix="点"
                prefix={<ShoppingOutlined />}
                valueStyle={{ color: token.colorInfo, fontSize: '20px' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="在庫総額"
                value={totalStockValue}
                prefix={<DollarOutlined />}
                precision={0}
                valueStyle={{ color: token.colorSuccess, fontSize: '20px' }}
                formatter={(value) => `¥${Number(value).toLocaleString()}`}
              />
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Statistic
                title="在庫切れ"
                value={outOfStockCount}
                suffix="点"
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: token.colorError, fontSize: '20px' }}
              />
            </Col>
            <Col span={12}>
              <Statistic
                title="在庫不足"
                value={lowStockCount}
                suffix="点"
                prefix={<WarningOutlined />}
                valueStyle={{ color: token.colorWarning, fontSize: '20px' }}
              />
            </Col>
          </Row>
        </>
      )}
    </Card>
  );
}
